import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ProductCard from '../../components/catalog/ProductCard';
import RequestReasonModal from '../../components/catalog/RequestReasonModal';

export default function ProductCatalogPage() {
  const navigate = useNavigate();
  const { user, token, fetchUserProfile } = useAuth();
  const location = useLocation();
  const isSidebarOpen = location?.state?.isSidebarOpen;
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canCreateCampaign = user?.canCreateCampaign ?? false;

  useEffect(() => {
    const fetchProducts = async () => {
      if (!token) {
        setLoading(false);
        setError("You must be logged in to view the catalog.");
        return;
      }
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/products`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Could not fetch the product catalog.');
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [token]);

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleReasonSubmit = async (reason) => {
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ productId: selectedProduct.id, reason: reason })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to submit your campaign request.');
      
      alert("Your campaign request has been submitted for review!");
      await fetchUserProfile(token); // Refresh user state to update canCreateCampaign flag
      handleCloseModal();
      navigate('/recipient/dashboard');
    } catch (err) {
      setError(err.message);
      handleCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center p-16 text-xl">Loading Product Catalog...</div>;
  }

  return (
    <div className={`p-6 md:p-10 overflow-auto ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
      <h1 className="text-4xl font-extrabold text-gray-800 dark:text-white">Product Catalog</h1>
      
      {error && <p className="mt-4 text-red-500 font-semibold">{error}</p>}

      {/* ✅ UPDATED: Show different messages based on the new flag */}
      {!canCreateCampaign && !loading ? (
        <p className="mt-2 text-yellow-500">You already have an active request. View its status on your dashboard.</p>
      ) : (
        <p className="mt-2 text-gray-500 dark:text-gray-400">Please select one item you would like to request.</p>
      )}

      {products.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mt-12">
          {products.map(product => (
            <ProductCard 
              key={product.id} 
              product={product} 
              onSelect={handleSelectProduct}
              // ✅ UPDATED: The disabled logic is now much cleaner
              disabled={!canCreateCampaign}
            />
          ))}
        </div>
      ) : (
        !loading && <p className="mt-12 text-lg text-gray-500">No products are available in the catalog at this time.</p>
      )}

      {isModalOpen && (
        <RequestReasonModal 
          product={selectedProduct} 
          onClose={handleCloseModal}
          onSubmit={handleReasonSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}